import React, { useState, useEffect } from "react";
import Dropdown from "./Dropdown";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { createTicket } from "../../app/api/admin/tickets";
import { dropDownData } from "../../app/api/admin/tickets";
import { AxiosError } from "axios";
import { ReactComponent as Close } from "../../assets/icons/close.svg";
import PostReport from "../Tickets/PostReport";
import TrendReport from "../Tickets/TrendsReport";
import { getProductTypes, getStylingDetails } from "../../app/api/trendsFilter";

type Props = {
  imageUrl: string;
  source: string;
  liveSince: string;
  postId: string;
  closeView: () => void;
};

const ReportExpandView: React.FC<Props> = ({
  imageUrl,
  source,
  liveSince,
  postId,
  closeView,
}) => {
  const [issues, setIssues] = useState<string[]>([]);
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [stylingDetailsList, setStylingDetailsList] = useState<string[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<string>("Issue");
  const [selectedProductType, setSelectedProductType] = useState<string>(""); // get from session storage and make api call to get product type
  const [selectedStylingDetail, setSelectedStylingDetail] =
    useState<string>(""); // get from session storage and make api call to get trend type
  const [selectedErrorType, setSelectedErrorType] = useState<string>("");
  const [selectedSeverity, setSelectedSeverity] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [isTrendAnalysisIssue, setIsTrendAnalysisIssue] =
    useState<boolean>(false);
  const user = useSelector((state: RootState) => state.user.userInfo);

  const handleIssueSelect = (issue: string) => {
    const trends = sessionStorage.getItem("trends");
    if (issue === "Incorrect Trend analysis" && !trends) {
      toast.error("Run Trend Analysis to enable this option", {
        hideProgressBar: true,
      });
      setSelectedIssue("Issue");
      setIsTrendAnalysisIssue(false);
      return;
    }
    setSelectedIssue(issue);
    setIsTrendAnalysisIssue(issue === "Incorrect Trend analysis");
  };

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        const issuesData = await dropDownData();
        if (issuesData) {
          setIssues(
            issuesData.map((issueObj: { issue: string }) => issueObj.issue)
          );
        }
      } catch (error) {
        if ((error as AxiosError).response?.status !== 400) {
          console.error("Error fetching issues:", error);
          toast.error("Failed to load issues.", {
            hideProgressBar: true,
          });
        }
      }
    };
    // fetch product type, trend type
    const trends = JSON.parse(sessionStorage.getItem("trends") ?? "{}");
    const fetchProductTypes = async () => {
      try {
        const productTypesData = await getProductTypes(
          {
            category: trends.category,
            gender: trends.gender,
          },
          user
        );
        setSelectedProductType(trends.apparel_type);
        setProductTypes(productTypesData.data);
      } catch (error) {
        console.error("Error fetching product types:", error);
        toast.error("Failed to load product types.", {
          hideProgressBar: true,
        });
      }
    };
    const fetchStylingDetails = async () => {
      try {
        const trendTypesData = await getStylingDetails(
          {
            category: trends.category,
            gender: trends.gender,
            productType: trends.apparel_type,
          },
          user
        );
        const extraValues = ["Color", "Pattern", "Embroidery", "Fabric"];

        // Append extra values to the fetched data
        const combinedList = [...extraValues, ...trendTypesData.data];

        setStylingDetailsList(combinedList);
      } catch (error) {
        console.error("Error fetching trend types:", error);
        toast.error("Failed to load trend types.", {
          hideProgressBar: true,
        });
      }
    };
    if (trends.category && trends.gender) {
      fetchProductTypes();
      const stylingDetailValueSelected = Object.keys(trends)
        .filter(key => trends[key] === true || trends[key] === "shape")
        .map(key => {
          let formattedKey = key
            .replace(/([a-z])([A-Z])/g, "$1 $2") // split camelCase
            .replace(/_/g, " ") // replace underscores with space
            .replace(/\b\w/g, char => char.toUpperCase()); // capitalize each word

          if (key === "shapes") {
            formattedKey = formattedKey.replace(/\bShapes\b/i, "Styles");
          } else if (key === "sleeves") {
            formattedKey = formattedKey.replace(
              /\bSleeves\b/i,
              "Sleeve Details"
            );
          }
          return formattedKey;
        });

      if (trends.apparel_type !== "All") {
        setSelectedStylingDetail(stylingDetailValueSelected[0] || "");
      }
      setStylingDetailsList(stylingDetailValueSelected);
      // fetchStylingDetails();
    }
    fetchIssues();
  }, []);
  const handleSeverityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSeverity(event.target.value);
  };

  const handleReasonChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    if (event.target.value.length <= 1000) setReason(event.target.value);
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error("User details not available. Please login.", {
        hideProgressBar: true,
      });
      return;
    }

    if (selectedIssue === "Issue") {
      toast.error("Please select an issue.", {
        hideProgressBar: true,
      });
      return;
    }

    if (!isTrendAnalysisIssue && !selectedSeverity) {
      toast.error("Please select severity.", {
        hideProgressBar: true,
      });
      return;
    }

    const ticketData = {
      image: imageUrl,
      source: source || "uploaded",
      issueType: selectedIssue,
      liveSince: liveSince || "",
      createdBy: user.name,
      priority: selectedSeverity ? selectedSeverity : "High",
      email: user.email,
      reason,
      status: "open",
      postId: String(postId ?? "").trim(),
      contentId: String(postId ?? "").trim(),
      userId: user.userId,
      ticketMarked: "default",
      productType: selectedProductType,
      stylingDetails: selectedStylingDetail,
      errorType: selectedErrorType,
    };
    try {
      await createTicket(ticketData);
      toast.success("Report submitted successfully!", {
        hideProgressBar: true,
      });
    } catch (error) {
      const status = (error as AxiosError).response?.status;
      if (status === 409) {
        toast.error("Post is already reported by you", {
          hideProgressBar: true,
        });
      } else if (status !== 400) {
        toast.error("Failed to submit report. Please try again.", {
          hideProgressBar: true,
        });
      }
      console.error("Error creating ticket:", error);
    }
    closeView();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black opacity-50"
        onClick={() => closeView()}
      ></div>

      <div
        className=" relative bg-white rounded-lg shadow-sm z-60 font-poppins p-4"
        style={{
          width: "483px",
          height: `${isTrendAnalysisIssue ? "560px" : "410px"}`,
          overflow: "hidden",
        }}
      >
        <div className="flex justify-between">
          <h1 className="text-xl font-bold">Report</h1>
          <button
            onClick={() => closeView()}
            className="text-gray-500 hover:text-gray-700"
          >
            <Close />
          </button>
        </div>
        <div className="flex flex-col justify-start w-[448px] h-[350px] bg-gray-100 font-poppins ">
          <div className="w-full mb-4 space-y-2 mt-4">
            <p className="text-sm font-poppins">Issue Type</p>
            <Dropdown
              label="Select an issue"
              options={issues}
              onSelect={handleIssueSelect}
            />
          </div>

          {isTrendAnalysisIssue ? (
            <TrendReport
              selectedProductType={selectedProductType}
              setSelectedErrorType={setSelectedErrorType}
              setSelectedProductType={setSelectedProductType}
              setSelectedStylingDetail={setSelectedStylingDetail}
              productTypes={productTypes}
              stylingDetailList={stylingDetailsList}
            />
          ) : (
            <PostReport
              selectedSeverity={selectedSeverity}
              onChange={handleSeverityChange}
            />
          )}

          <div className="w-full  space-y-2">
            <p className="text-sm font-poppins">Select Reason (optional) </p>
            <textarea
              value={reason}
              onChange={handleReasonChange}
              rows={3}
              draggable="false"
              className="w-full p-2 border border-gray-300 rounded resize-none"
              placeholder="Explain the reason if needed..."
            />
            <p className="text-sm font-poppins">{reason.length}/1000</p>
          </div>

          <div className="m-auto">
            <input
              type="button"
              value="Submit Report"
              onClick={handleSubmit}
              className="text-sm font-poppins self-end px-4 py-2 bg-black text-white rounded-full  cursor-pointer"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportExpandView;
